/**
 * @fileoverview Flash message hook.
 * @description Holds a confirmation message that clears itself a few seconds after it is shown, or
 * when it is dismissed. Showing a new message restarts the timer.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useCallback, useEffect, useState } from 'react'

const DEFAULT_DURATION_MS = 5000

export interface FlashMessage {
  /** The message now showing, or '' when there is none. */
  message: string
  /** Shows a message, replacing any showing now and restarting the timer. */
  show: (message: string) => void
  /** Clears the message at once. */
  dismiss: () => void
}

/** A message and which showing of it this is, so showing the same text again is a new showing. */
interface Flash {
  text: string
  showing: number
}

/**
 * @description Keeps a flash message that clears itself.
 * @param {number} [durationMs] - How long a message stays. Defaults to five seconds.
 * @returns {FlashMessage} The message, and how to show and dismiss it.
 */
export function useFlashMessage(durationMs = DEFAULT_DURATION_MS): FlashMessage {
  const [flash, setFlash] = useState<Flash>({ text: '', showing: 0 })

  /**
   * @description Clears the message after the duration, counted from the latest showing (so a
   * message identical to the one already showing still restarts the timer).
   * @returns {(() => void) | undefined} Cancels the pending clear when a newer showing replaces
   * this one or the component unmounts.
   */
  useEffect(() => {
    if (!flash.text) {
      return
    }
    /**
     * @description Clears the message.
     */
    const clear = () => {
      setFlash((current) => ({ ...current, text: '' }))
    }
    const timer = setTimeout(clear, durationMs)
    /**
     * @description Cancels the pending clear.
     */
    return () => {
      clearTimeout(timer)
    }
  }, [flash.text, flash.showing, durationMs])

  /**
   * @description Shows a message, starting a new showing even when the text is the same.
   * @param {string} next - The message.
   */
  const show = useCallback((next: string) => {
    setFlash((current) => ({ text: next, showing: current.showing + 1 }))
  }, [])

  /**
   * @description Clears the message.
   */
  const dismiss = useCallback(() => {
    setFlash((current) => ({ ...current, text: '' }))
  }, [])

  return { message: flash.text, show, dismiss }
}
